import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { SiexApiService } from '../../../../../service/api/siex-api.service';
import { build, pool } from '../../../../../common/classes/request-builder';
import { environment } from '../../../../../../environments/environment';
import { StorageManager } from '../../../../../common/storage-manager.class';
import type {
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { OptionsComponent } from '../../../../../common/components/upload-file/upload-file.component';
import { AgroambientalModel } from '../../../../../models/tareas/agroambientales.model';
import { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import { AgroambientalesFormDefinition } from './form-control/form-definition';

@Component({
    selector: 'app-agroambientales-form',
    standalone: false,
    templateUrl: './agroambientales-form.component.html',
    styleUrls: ['./agroambientales-form.component.scss'],
})
export class AgroambientalesFormComponent extends BaseForm<AgroambientalModel> implements OnInit {
    public readonly appName = environment.appName;

    public form!: AgroambientalesFormDefinition;
    public override model: AgroambientalModel = {};
    public formReady = false;
    public loadingData = true;
    public showDialogUploadFile = false;

    public optionsUploadFile: OptionsComponent = {
        name: 'u_' + (environment.features.hasClients ? StorageManager.getClient()?.id : StorageManager.getUser()?.id),
        directory: 'ecorregimenes',
        multiple: false,
        accept: '.pdf',
        maxFileSize: 2000000,
        mode: 'advanced',
        uploaded: false,
        uploadedFilesNames: [],
        url: environment.serverUrl + 'ws/index.php?p1=upload&p2=upload_file&folder=ecorregimenes',
    };

    constructor(
        public override dashboard: DashboardService,
        public tareasApi: TareasApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public fincasApi: FincasApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public siexApi: SiexApiService,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.agroambientales.PUT,
            tareasApi.agroambientales.POST,
            'tareas-agroambientales',
            `Editar Tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[0] ?? 'Agroambientales'}`,
            `Añadir datos ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[0] ?? 'Agroambientales'}`,
            `Duplicar tareas ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[0] ?? 'Agroambientales'}`,
        );
    }

    public async ngOnInit(): Promise<void> {
        this.softInit(this.getType());
        this.form = new AgroambientalesFormDefinition(this.getType(), this.getModel());
        this.form.model = this.model;

        await this.initFormData();

        this.optionsUploadFile.uploaded = Boolean(this.model.adjunto_guia);
        this.optionsUploadFile.uploadedFilesNames = this.model.adjunto_guia
            ? String(this.model.adjunto_guia).split(',').map((it) => it.trim()).filter((it) => it.length > 0)
            : [];

        this.formReady = true;
    }

    public formChanges(
        formModel: ReactiveFormValue<AgroambientalModel>,
        tag: ReactiveFormChangedField<AgroambientalModel>,
    ): void {
        const changedFieldName = String(tag);

        this.form.model = {
            ...this.form.model,
            ...formModel,
        } as AgroambientalModel;
        this.model = this.form.model as AgroambientalModel;

        if (changedFieldName === 'tipo_ecorregimen') {
            this.form.syncConditionalVisibility();
        }

        if (changedFieldName === 'id_finca') {
            this.clearLocationChildren(['id_sector', 'id_parcelas']);
        }

        if (changedFieldName === 'id_sector') {
            this.clearLocationChildren(['id_parcelas']);
        }
    }

    public handleEventUpload(event: { originalEvent: unknown; files: File[] }): void {
        if (event.files.length === 0) {
            return;
        }

        const adjuntoGuia = event.files.map((file) => file.name).join(', ');

        this.model.adjunto_guia = adjuntoGuia;
        this.form.model.adjunto_guia = adjuntoGuia;
        this.optionsUploadFile.uploaded = true;
        this.optionsUploadFile.uploadedFilesNames = event.files.map((file) => file.name);
        this.showDialogUploadFile = false;
        this.formBuilderRef?.updateForm({ adjunto_guia: adjuntoGuia }, true);
    }

    public isPastoreoSelected(): boolean {
        return this.form?.isPastoreoSelected() ?? false;
    }

    private initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        return pool([
            build(this.fincasApi.fincasGET, this.form.fincas, 'nombre'),
            build(this.fincasApi.sectoresGET, this.form.sectores, 'nombre', true),
            build(
                this.fincasApi.parcelasGET,
                this.form.parcelas,
                this.appName === 'avafitogest' ? this.parcelaLabel : 'nombre',
                true,
                true,
            ),
            build(this.siexApi.actividadesSobreCubiertaGET, this.form.actividadesSobreCubierta, 'actividad_sobre_la_cubierta'),
            build(this.siexApi.especiesAnimalesGET, this.form.especiesAnimales, 'especies_animales', true),
            build(this.trabajadoresApi.maquinariaGET, this.form.maquinarias, 'nombre', true),
        ], () => {
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();

            return true;
        }).catch((error: unknown) => {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();
            console.error('Error loading agroambientales form data.', error);

            return true;
        });
    }

    private clearLocationChildren(fieldNames: string[]): void {
        const nextModel = { ...this.form.model } as Record<string, unknown>;

        for (const fieldName of fieldNames) {
            nextModel[fieldName] = '';
        }

        this.form.model = nextModel as AgroambientalModel;
        this.model = this.form.model as AgroambientalModel;
        this.formBuilderRef?.updateForm(nextModel, false);
    }

    private parcelaLabel = (p: ParcelasModel): string => {
        const nombre = (p.nombre ?? '').toString().trim();
        const variedad = (p.variedad ?? 'SIN VARIEDAD').toString().trim();

        let zonaRiegoAbrev = '';

        if ((p.cultivo ?? '').toLowerCase() === 'arroz') {
            const zonaRiego = (p.zona_riego ?? '').toString().toLowerCase();

            if (zonaRiego.includes('alta')) {
                zonaRiegoAbrev = 'ZA';
            } else if (zonaRiego.includes('baja')) {
                zonaRiegoAbrev = 'ZB';
            }
        }

        const variedadConZona = zonaRiegoAbrev
            ? `${variedad} - ${zonaRiegoAbrev}`
            : variedad;

        const poligono = (p.poligono ?? '').toString().trim();
        const numero = (p.numero ?? '').toString().trim();
        const recinto = (p.recinto ?? '').toString().trim();
        const codigos = [poligono, numero, recinto].filter(Boolean).join('-');
        const superficieRaw = (p.superficie_cultivada ?? p.superficie_sigpac ?? p.superficie ?? '0').toString();
        const superficie = parseFloat(superficieRaw.replace(',', '.')) || 0;
        const superficieFormatted = superficie.toLocaleString('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        const unidades = environment.features.textoSuperficie || 'ha';
        const rightText = [codigos || undefined, `${superficieFormatted} ${unidades}`]
            .filter(Boolean)
            .join(' · ');

        return `${nombre} - ${variedadConZona} — ${rightText}`;
    };
}
